// src/features/calendar/settings/CalendarFormDialog.tsx
// Create / edit a calendar — one form-kit dialog shared by both flows
// (docs/ux/14-forms.md). Covers the full Calendar surface: name, description,
// icon, color, type, visibility, timezone, and the rare per-calendar event
// defaults behind "+ more fields".

import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from '@react';
import { useCalendar, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, FormRow, SegmentedControl, TitleInput, AutoGrowTextarea } from '@/components/form';
import { Select } from '@/components/Select';
import { EmojiPicker } from '@/components/EmojiPicker';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { TimezoneSelect } from '../TimezoneSelect';
import { useScope } from '@/hooks/useScope';
import type {
  Calendar,
  CalendarType,
  CalendarVisibility,
  EventVisibility,
  ReminderMethod,
} from '@core/types/calendar';
import { durationToMinutes, errMessage, makeEntity, minutesToDuration } from '../time';

export interface CalendarFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Calendar being edited; null → create. Must be the full record (not a trimmed list item). */
  calendar: Calendar | null;
  onSaved: () => void;
}

const TYPES: CalendarType[] = [
  'CALENDAR_TYPE_PERSONAL', 'CALENDAR_TYPE_TEAM', 'CALENDAR_TYPE_PROJECT',
  'CALENDAR_TYPE_SHARED', 'CALENDAR_TYPE_HOLIDAY',
];
const VISIBILITIES: CalendarVisibility[] = [
  'CALENDAR_VISIBILITY_PRIVATE', 'CALENDAR_VISIBILITY_INTERNAL', 'CALENDAR_VISIBILITY_PUBLIC',
];
const EVENT_VISIBILITIES: EventVisibility[] = [
  'EVENT_VISIBILITY_DEFAULT', 'EVENT_VISIBILITY_PUBLIC', 'EVENT_VISIBILITY_PRIVATE', 'EVENT_VISIBILITY_CONFIDENTIAL',
];
const REMINDER_METHODS: ReminderMethod[] = [
  'REMINDER_METHOD_IN_APP', 'REMINDER_METHOD_EMAIL', 'REMINDER_METHOD_PUSH', 'REMINDER_METHOD_SMS',
];

// Same 16-swatch palette used for team-role colors — kept consistent app-wide.
const COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
  '#78716c',
];

const typeKey = (v: string) => v.replace('CALENDAR_TYPE_', '').toLowerCase();
const visKey = (v: string) => v.replace('CALENDAR_VISIBILITY_', '').toLowerCase();
const eventVisKey = (v: string) => v.replace('EVENT_VISIBILITY_', '').toLowerCase();
const reminderKey = (v: string) => v.replace('REMINDER_METHOD_', '').toLowerCase();

interface FormState {
  name: string;
  description: string;
  icon: string;
  color: string;
  type: CalendarType;
  visibility: CalendarVisibility;
  timezone: string;
  defaultDurationMinutes: string;
  defaultReminderMethod: ReminderMethod;
  defaultReminderMinutes: string;
  defaultVisibility: EventVisibility;
  weekStartDay: 0 | 1;
  workingHoursEnabled: boolean;
  autoDeclineConflicts: boolean;
  autoAddMeetLink: boolean;
}

function snapshotOf(cal: Calendar | null): FormState {
  const s = cal?.settings;
  return {
    name: cal?.name ?? '',
    description: cal?.description ?? '',
    icon: cal?.icon ?? '',
    color: cal?.color ?? '',
    type: cal?.type ?? 'CALENDAR_TYPE_PERSONAL',
    visibility: cal?.visibility ?? 'CALENDAR_VISIBILITY_PRIVATE',
    timezone: cal?.timezone ?? (cal ? '' : Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''),
    defaultDurationMinutes: String(durationToMinutes(s?.default_event_duration)),
    defaultReminderMethod: s?.default_reminder?.method ?? 'REMINDER_METHOD_IN_APP',
    defaultReminderMinutes: s?.default_reminder ? String(s.default_reminder.minutes_before) : '',
    defaultVisibility: s?.default_visibility ?? 'EVENT_VISIBILITY_DEFAULT',
    weekStartDay: (s?.week_start_day === 1 ? 1 : 0) as 0 | 1,
    workingHoursEnabled: s?.working_hours_enabled ?? false,
    autoDeclineConflicts: s?.auto_decline_conflicts ?? false,
    autoAddMeetLink: s?.auto_add_meet_link ?? false,
  };
}

export const CalendarFormDialog = observer(({ open, onClose, calendar, onSaved }: CalendarFormDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendar();
  const isEdit = !!calendar;
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [form, setForm] = useState<FormState>(() => snapshotOf(calendar));
  const initialRef = useRef<FormState>(snapshotOf(calendar));
  const [moreFields, setMoreFields] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const snap = snapshotOf(calendar);
    setForm(snap);
    initialRef.current = snap;
    setMoreFields(false);
    setNameTouched(false);
    setError(null);
  }, [open, calendar]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialRef.current), [form]);
  const canSubmit = form.name.trim().length > 0;

  const save = async () => {
    if (!canSubmit) {
      setNameTouched(true);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const settings = {
        default_event_duration: minutesToDuration(form.defaultDurationMinutes === '' ? '' : Number(form.defaultDurationMinutes)),
        default_reminder: form.defaultReminderMinutes !== ''
          ? { method: form.defaultReminderMethod, minutes_before: Number(form.defaultReminderMinutes) }
          : undefined,
        default_visibility: form.defaultVisibility,
        week_start_day: form.weekStartDay,
        working_hours_enabled: form.workingHoursEnabled,
        auto_decline_conflicts: form.autoDeclineConflicts,
        auto_add_meet_link: form.autoAddMeetLink,
      };

      if (isEdit && calendar) {
        await store.UpdateCalendar(calendar.entity?.id ?? '', {
          calendar: {
            ...calendar,
            name: form.name.trim(),
            description: form.description || undefined,
            color: form.color || undefined,
            icon: form.icon || undefined,
            type: form.type,
            visibility: form.visibility,
            timezone: form.timezone || undefined,
            settings,
          },
          field_mask: ['name', 'description', 'color', 'icon', 'type', 'visibility', 'timezone', 'settings'],
        });
      } else {
        const created = await store.CreateCalendar({
          entity: makeEntity('', 'calendar', scope),
          name: form.name.trim(),
          description: form.description || undefined,
          color: form.color || undefined,
          type: form.type,
          visibility: form.visibility,
          timezone: form.timezone || undefined,
          settings,
          metadata: {},
        });
        // CreateCalendarRequest has no icon field on the wire yet (Calendar /
        // UpdateCalendar do) — patch it in immediately so the icon picker
        // isn't silently dropped on create.
        if (form.icon && created?.entity?.id) {
          await store.UpdateCalendar(created.entity.id, {
            calendar: { ...created, icon: form.icon },
            field_mask: ['icon'],
          });
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('calendar.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={save}
      submitLabel={isEdit ? t('common.save') : t('calendar.calendarsSettings.newCalendar')}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('calendar.calendarsSettings.editCalendar') : t('calendar.calendarsSettings.newCalendar')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('calendar.calendarsSettings.fewerFields') : t('calendar.calendarsSettings.moreFields')}
        </button>
      }
    >
      <div className="space-y-3 px-4 pb-1 pt-2">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="flex items-start gap-2">
          <EmojiPicker
            value={form.icon}
            onChange={(icon) => set('icon', icon)}
            placeholder={t('calendar.field.iconPlaceholder')}
            searchPlaceholder={t('common.search')}
            noResultsLabel={t('common.noResults')}
            clearLabel={t('common.clear')}
          />
          <div className="min-w-0 flex-1">
            <TitleInput
              value={form.name}
              onChange={(v) => set('name', v)}
              placeholder={t('calendar.field.namePlaceholder')}
              initialFocus
              onEnter={() => descRef.current?.focus()}
              onBlur={() => setNameTouched(true)}
            />
            {nameTouched && !canSubmit && (
              <p className="mt-0.5 text-xs text-destructive">{t('calendar.calendarsSettings.nameRequired')}</p>
            )}
          </div>
        </div>

        <AutoGrowTextarea
          ref={descRef}
          value={form.description}
          onChange={(v) => set('description', v)}
          placeholder={t('calendar.field.descriptionPlaceholder')}
        />
      </div>

      {/* Property row */}
      <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            title={t('calendar.field.colorAuto')}
            onClick={() => set('color', '')}
            className={`grid h-7 w-7 place-items-center rounded-full border-2 border-dashed border-muted-foreground/40 text-muted-foreground/60 transition-transform ${!form.color ? 'ring-2 ring-offset-2 ring-foreground scale-110' : ''}`}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => set('color', c)}
              title={c}
              className={`h-7 w-7 rounded-full transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${form.color === c ? 'ring-2 ring-offset-2 ring-foreground scale-110' : ''}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>

        <Select
          aria-label={t('calendar.field.type')}
          value={form.type}
          onChange={(e) => set('type', e.target.value as CalendarType)}
          options={TYPES.map((v) => ({ value: v, label: t(`calendar.type.${typeKey(v)}`) }))}
          fullWidth={false}
          className="w-36"
        />

        <SegmentedControl
          aria-label={t('calendar.field.visibility')}
          size="sm"
          value={form.visibility}
          onChange={(v) => set('visibility', v as CalendarVisibility)}
          options={VISIBILITIES.map((v) => ({ value: v, label: t(`calendar.visibilityLevel.${visKey(v)}`) }))}
        />

        <TimezoneSelect
          value={form.timezone}
          onChange={(tz) => set('timezone', tz)}
          placeholder={t('calendar.field.timezoneAuto')}
          searchPlaceholder={t('common.search')}
          noResultsLabel={t('common.noResults')}
          clearLabel={t('common.clear')}
          autoLabel={t('calendar.field.timezoneAuto')}
          aria-label={t('calendar.field.timezone')}
          className="w-48"
        />
      </div>

      {moreFields && (
        <div className="space-y-1 border-t border-border px-4 py-2">
          <p className="pb-1 pt-1 text-xs font-medium text-muted-foreground">
            {t('calendar.calendarsSettings.defaultsSection')}
          </p>

          <FormRow label={t('calendar.calendarsSettings.defaultDuration')} labelWidth="sm" hint={t('calendar.calendarsSettings.defaultDurationHint')}>
            <span className="inline-flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={form.defaultDurationMinutes}
                onChange={(e) => set('defaultDurationMinutes', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="—"
                aria-label={t('calendar.calendarsSettings.defaultDuration')}
                className="w-20 rounded-md border border-input bg-background px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <span className="text-xs text-muted-foreground">{t('calendar.calendarsSettings.minutesUnit')}</span>
              {form.defaultDurationMinutes && (
                <button type="button" onClick={() => set('defaultDurationMinutes', '')} aria-label={t('common.clear')} className="text-xs text-muted-foreground hover:text-foreground">✕</button>
              )}
            </span>
          </FormRow>

          <FormRow label={t('calendar.calendarsSettings.defaultReminder')} labelWidth="sm">
            <span className="inline-flex items-center gap-2">
              <Select
                aria-label={t('calendar.event.reminderMethod')}
                value={form.defaultReminderMethod}
                onChange={(e) => set('defaultReminderMethod', e.target.value as ReminderMethod)}
                options={REMINDER_METHODS.map((v) => ({ value: v, label: t(`calendar.reminderMethod.${reminderKey(v)}`) }))}
                fullWidth={false}
                className="w-36"
              />
              <input
                type="text"
                inputMode="numeric"
                value={form.defaultReminderMinutes}
                onChange={(e) => set('defaultReminderMinutes', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="—"
                aria-label={t('calendar.event.reminderMinutes')}
                className="w-20 rounded-md border border-input bg-background px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <span className="text-xs text-muted-foreground">{t('calendar.event.minutesBefore')}</span>
              {form.defaultReminderMinutes && (
                <button type="button" onClick={() => set('defaultReminderMinutes', '')} aria-label={t('common.clear')} className="text-xs text-muted-foreground hover:text-foreground">✕</button>
              )}
            </span>
          </FormRow>

          <FormRow label={t('calendar.calendarsSettings.defaultEventVisibility')} labelWidth="sm">
            <SegmentedControl
              aria-label={t('calendar.calendarsSettings.defaultEventVisibility')}
              size="sm"
              value={form.defaultVisibility}
              onChange={(v) => set('defaultVisibility', v as EventVisibility)}
              options={EVENT_VISIBILITIES.map((v) => ({ value: v, label: t(`calendar.visibilityValue.${eventVisKey(v)}`) }))}
            />
          </FormRow>

          <FormRow label={t('calendar.calendarsSettings.weekStart')} labelWidth="sm">
            <SegmentedControl
              aria-label={t('calendar.calendarsSettings.weekStart')}
              size="sm"
              value={String(form.weekStartDay)}
              onChange={(v) => set('weekStartDay', (v === '1' ? 1 : 0) as 0 | 1)}
              options={[
                { value: '0', label: t('calendar.weekday.sun') },
                { value: '1', label: t('calendar.weekday.mon') },
              ]}
            />
          </FormRow>

          <div className="space-y-2 pt-1">
            <ToggleSwitch
              label={t('calendar.calendarsSettings.workingHoursEnabled')}
              description={t('calendar.calendarsSettings.workingHoursEnabledHint')}
              checked={form.workingHoursEnabled}
              onChange={(v) => set('workingHoursEnabled', v)}
            />
            <ToggleSwitch
              label={t('calendar.calendarsSettings.autoDeclineConflicts')}
              description={t('calendar.calendarsSettings.autoDeclineConflictsHint')}
              checked={form.autoDeclineConflicts}
              onChange={(v) => set('autoDeclineConflicts', v)}
            />
            <ToggleSwitch
              label={t('calendar.calendarsSettings.autoAddMeetLink')}
              description={t('calendar.calendarsSettings.autoAddMeetLinkHint')}
              checked={form.autoAddMeetLink}
              onChange={(v) => set('autoAddMeetLink', v)}
            />
          </div>
        </div>
      )}
    </FormDialog>
  );
});

export default CalendarFormDialog;
